/**
 * Title/Escrow Company Form
 *
 * Create/Edit form with tabs for company info and fee schedule.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Save, Building2, DollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  TitleEscrowCompany,
  CompanyType,
  CreateTitleEscrowCompanyInput
} from '@/types/title-escrow';
import { titleEscrowService } from '@/services/titleEscrowService';
import { FeeScheduleTab } from './FeeScheduleTab';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('TitleEscrowForm', 'title-escrow');

// US States for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

// Form schema
const companySchema = z.object({
  name: z.string().min(1, 'Company name is required'),
  companyType: z.enum(['title', 'escrow', 'both'] as const),
  legalName: z.string().optional(),
  addressLine1: z.string().min(1, 'Street address is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().length(2, 'State is required'),
  zip: z.string().min(5, 'ZIP code is required'),
  contactFirstName: z.string().optional(),
  contactLastName: z.string().optional(),
  mainPhone: z.string().optional(),
  mainEmail: z.string().email().optional().or(z.literal('')),
  fax: z.string().optional(),
  website: z.string().optional(),
  officerFirstName: z.string().optional(),
  officerLastName: z.string().optional(),
  officerEmail: z.string().email().optional().or(z.literal('')),
  officerPhone: z.string().optional(),
  isPreferred: z.boolean().default(false),
  notes: z.string().optional()
});

type CompanyFormData = z.infer<typeof companySchema>;

interface TitleEscrowFormProps {
  companyId: string | null;
  onBack: () => void;
  onSaved: () => void;
}

export const TitleEscrowForm: React.FC<TitleEscrowFormProps> = ({
  companyId,
  onBack,
  onSaved
}) => {
  const [loading, setLoading] = useState(!!companyId);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState<TitleEscrowCompany | null>(null);
  const [activeTab, setActiveTab] = useState('info');

  const isEdit = !!companyId;

  const form = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      companyType: 'title',
      legalName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      contactFirstName: '',
      contactLastName: '',
      mainPhone: '',
      mainEmail: '',
      fax: '',
      website: '',
      officerFirstName: '',
      officerLastName: '',
      officerEmail: '',
      officerPhone: '',
      isPreferred: false,
      notes: ''
    }
  });

  // Load company data if editing
  useEffect(() => {
    if (companyId) {
      loadCompany(companyId);
    }
  }, [companyId]);

  const loadCompany = async (id: string) => {
    try {
      setLoading(true);
      const data = await titleEscrowService.getCompanyById(id);
      if (data) {
        setCompany(data);
        form.reset({
          name: data.name,
          companyType: data.companyType,
          legalName: data.legalName || '',
          addressLine1: data.addressLine1,
          addressLine2: data.addressLine2 || '',
          city: data.city,
          state: data.state,
          zip: data.zip,
          contactFirstName: data.contactFirstName || '',
          contactLastName: data.contactLastName || '',
          mainPhone: data.mainPhone || '',
          mainEmail: data.mainEmail || '',
          fax: data.fax || '',
          website: data.website || '',
          officerFirstName: data.officerFirstName || '',
          officerLastName: data.officerLastName || '',
          officerEmail: data.officerEmail || '',
          officerPhone: data.officerPhone || '',
          isPreferred: data.isPreferred,
          notes: data.notes || ''
        });
      }
    } catch (error) {
      log.error('Failed to load company', { id }, error as Error);
      toast.error('Failed to load company');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: CompanyFormData) => {
    try {
      setSaving(true);

      const input: CreateTitleEscrowCompanyInput = {
        name: data.name,
        companyType: data.companyType as CompanyType,
        legalName: data.legalName || undefined,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2 || undefined,
        city: data.city,
        state: data.state,
        zip: data.zip,
        contactFirstName: data.contactFirstName || undefined,
        contactLastName: data.contactLastName || undefined,
        mainPhone: data.mainPhone || undefined,
        mainEmail: data.mainEmail || undefined,
        fax: data.fax || undefined,
        website: data.website || undefined,
        officerFirstName: data.officerFirstName || undefined,
        officerLastName: data.officerLastName || undefined,
        officerEmail: data.officerEmail || undefined,
        officerPhone: data.officerPhone || undefined,
        isPreferred: data.isPreferred,
        notes: data.notes || undefined
      };

      if (isEdit && companyId) {
        await titleEscrowService.updateCompany(companyId, input);
        toast.success('Company updated');
      } else {
        const newCompany = await titleEscrowService.createCompany(input);
        setCompany(newCompany);
        toast.success('Company created');
        // Switch to edit mode to show fee schedule tab
        if (newCompany) {
          window.history.replaceState({}, '', `?edit=${newCompany.id}`);
        }
      }

      onSaved();
    } catch (error) {
      log.error('Failed to save company', {}, error as Error);
      toast.error('Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isEdit ? 'Edit Company' : 'Add Title/Escrow Company'}
            </h1>
            {company && (
              <p className="text-muted-foreground">{company.name}</p>
            )}
          </div>
        </div>
        <Button onClick={form.handleSubmit(onSubmit)} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Company
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="info">
            <Building2 className="w-4 h-4 mr-2" />
            Company Info
          </TabsTrigger>
          <TabsTrigger value="fees" disabled={!company}>
            <DollarSign className="w-4 h-4 mr-2" />
            Fee Schedule
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Company Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="name">Company Name *</Label>
                      <Input
                        id="name"
                        {...form.register('name')}
                        placeholder="WFG National Title"
                      />
                      {form.formState.errors.name && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.name.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="companyType">Company Type *</Label>
                      <Select
                        value={form.watch('companyType')}
                        onValueChange={(v) => form.setValue('companyType', v as CompanyType)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="title">Title Company</SelectItem>
                          <SelectItem value="escrow">Escrow Company</SelectItem>
                          <SelectItem value="both">Both (Title & Escrow)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="legalName">Legal Name</Label>
                      <Input
                        id="legalName"
                        {...form.register('legalName')}
                        placeholder="Optional legal business name"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Address */}
              <Card>
                <CardHeader>
                  <CardTitle>Address</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="addressLine1">Street Address *</Label>
                      <Input
                        id="addressLine1"
                        {...form.register('addressLine1')}
                        placeholder="650 E Hospitality Lane"
                      />
                      {form.formState.errors.addressLine1 && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.addressLine1.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="addressLine2">Suite/Unit</Label>
                      <Input
                        id="addressLine2"
                        {...form.register('addressLine2')}
                        placeholder="Suite 300"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        {...form.register('city')}
                        placeholder="San Bernardino"
                      />
                      {form.formState.errors.city && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.city.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Select
                        value={form.watch('state')}
                        onValueChange={(v) => form.setValue('state', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((s) => (
                            <SelectItem key={s.code} value={s.code}>
                              {s.code} - {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.formState.errors.state && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.state.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="zip">ZIP Code *</Label>
                      <Input
                        id="zip"
                        {...form.register('zip')}
                        placeholder="92408"
                      />
                      {form.formState.errors.zip && (
                        <p className="text-sm text-red-500 mt-1">
                          {form.formState.errors.zip.message}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Primary Officer */}
              <Card>
                <CardHeader>
                  <CardTitle>Primary Officer</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="officerFirstName">First Name</Label>
                      <Input
                        id="officerFirstName"
                        {...form.register('officerFirstName')}
                        placeholder="Lori"
                      />
                    </div>
                    <div>
                      <Label htmlFor="officerLastName">Last Name</Label>
                      <Input
                        id="officerLastName"
                        {...form.register('officerLastName')}
                        placeholder="Northcutt"
                      />
                    </div>
                    <div>
                      <Label htmlFor="officerEmail">Email</Label>
                      <Input
                        id="officerEmail"
                        type="email"
                        {...form.register('officerEmail')}
                        placeholder="teamlori@wfgtitleco.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="officerPhone">Phone</Label>
                      <Input
                        id="officerPhone"
                        {...form.register('officerPhone')}
                        placeholder="(909) 379-0923"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Company Contact */}
              <Card>
                <CardHeader>
                  <CardTitle>Company Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="contactFirstName">First Name</Label>
                      <Input
                        id="contactFirstName"
                        {...form.register('contactFirstName')}
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contactLastName">Last Name</Label>
                      <Input
                        id="contactLastName"
                        {...form.register('contactLastName')}
                        placeholder="Smith"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mainPhone">Phone</Label>
                      <Input
                        id="mainPhone"
                        {...form.register('mainPhone')}
                        placeholder="(866) 912-7710"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mainEmail">Email</Label>
                      <Input
                        id="mainEmail"
                        type="email"
                        {...form.register('mainEmail')}
                        placeholder="info@company.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      {...form.register('website')}
                      placeholder="https://www.company.com"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Status */}
              <Card>
                <CardHeader>
                  <CardTitle>Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isPreferred"
                      checked={form.watch('isPreferred')}
                      onCheckedChange={(checked) => form.setValue('isPreferred', !!checked)}
                    />
                    <Label htmlFor="isPreferred" className="cursor-pointer">
                      Preferred Provider
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Preferred providers are highlighted in company selection lists.
                  </p>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    {...form.register('notes')}
                    placeholder="Internal notes about this company..."
                    rows={4}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="fees" className="mt-6">
          {company && <FeeScheduleTab companyId={company.id} companyName={company.name} />}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TitleEscrowForm;
